import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ZeosPasswordInput } from "../../components/Shared/ZeosPasswordInput";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { useSignIn } from "../../hooks/useSignIn";
import { isValidEmail } from "../../services/environment/EnvironmentHelpers";
import { passwordValidationSchema } from "../../services/forms/validationSchemas/PasswordValidationSchema";
import { createLogger } from "../../services/Logger";
import { IZeosUserConfirmation } from "../../services/zeos-api/contracts/Account";
import { REQUEST_ERROR } from "../../services/zeos-api/ZeosApiExecutor";

type StatusState = {
  type: "success" | "error";
  message: string;
};

type ActivationPhase = "code" | "password";

/**
 * Activation for a user an admin created, kept deliberately outside the onboarding
 * route tree. Those users already belong to a company, so the onboarding steps have
 * nothing to ask them: they enter the code from their email, set a password, and land
 * on their portfolio.
 *
 * The emailed link carries no token or address -- the 6-digit code in the same email
 * stays the only secret -- so this screen asks for the address the code was sent to.
 */
export const AccountActivationScreen = () => {
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const log = useMemo(() => createLogger("AccountActivation"), []);

  const [phase, setPhase] = useState<ActivationPhase>("code");
  const [emailAddress, setEmailAddress] = useState("");
  const [code, setCode] = useState("");
  const [confirmedUserId, setConfirmedUserId] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // One toggle drives both fields: someone checking what they typed wants to see the
  // pair they are being asked to match.
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [status, setStatus] = useState<StatusState | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Countdown for the resend cooldown; the server's response is the source of truth
  // for the remaining seconds, this just ticks the UI down between calls.
  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  /**
   * Codes expire out of a recipient's reach -- deleted mail, a stale invite -- and this
   * screen carries no identity, so the resume endpoint (address only) is the one that
   * fits. It answers the same way whether or not the address is on file, so this never
   * confirms an account exists, and for an admin-created user it sends a link back here.
   */
  const resendCode = async () => {
    const trimmedEmail = emailAddress.trim();
    if (!isValidEmail(trimmedEmail)) {
      setStatus({ type: "error", message: "Enter your email address first." });
      return;
    }
    if (isResending || cooldownSeconds > 0) {
      return;
    }

    setIsResending(true);
    setStatus(null);
    try {
      const response = await api.Account.resumeVerificationCodeAsync({
        emailAddress: trimmedEmail,
      });

      if (!response.data) {
        setStatus({
          type: "error",
          message:
            response.message === REQUEST_ERROR
              ? "Failed to send a new code. Please try again."
              : response.message,
        });
        return;
      }

      setCooldownSeconds(response.data.cooldownSecondsRemaining);
      setStatus({
        type: "success",
        message:
          "If that address has an invitation waiting, a new code is on its way.",
      });
    } catch (error: any) {
      log.error("Activation resend failed", error);
      setStatus({ type: "error", message: "Failed to send a new code." });
    } finally {
      setIsResending(false);
    }
  };

  const submitCode = async () => {
    const trimmedEmail = emailAddress.trim();
    const trimmedCode = code.trim();

    if (!isValidEmail(trimmedEmail)) {
      setStatus({ type: "error", message: "Please enter a valid email address." });
      return;
    }
    if (!trimmedCode) {
      setStatus({ type: "error", message: "Enter the 6-digit code from your email." });
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      const verification: IZeosUserConfirmation = {
        // No identity is carried into this screen; the address and code are the whole
        // request and the response hands back the id the password step needs.
        zeosUserId: 0,
        accessCode: trimmedCode,
        emailAddress: trimmedEmail,
        password: "",
      };
      const response = await api.Account.postAccountVerificationAsync(verification);

      if (response.message === REQUEST_ERROR) {
        throw new Error("Something went wrong. Please try again.");
      }

      const confirmation = response.data;
      if (!confirmation?.verified || !confirmation.zeosUserId) {
        setStatus({
          type: "error",
          message: "That code or email address did not match. Please check and try again.",
        });
        return;
      }

      setConfirmedUserId(confirmation.zeosUserId);
      setEmailAddress(confirmation.emailAddress || trimmedEmail);
      setCode(trimmedCode);
      setPhase("password");
    } catch (error: any) {
      log.error("Activation code check failed", error);
      setStatus({ type: "error", message: error?.message || "Verification failed." });
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async () => {
    setStatus(null);
    try {
      await passwordValidationSchema.validate({ password, confirmPassword });
    } catch (validationError: any) {
      setStatus({
        type: "error",
        message: validationError?.message || "Please choose a valid password.",
      });
      return;
    }

    setLoading(true);
    try {
      const init: IZeosUserConfirmation = {
        zeosUserId: confirmedUserId,
        accessCode: code,
        emailAddress,
        password,
      };
      const setupResult = await api.Account.postAccountSetupAsync(init);
      if (
        !setupResult.data ||
        setupResult.zeosStatus === "error" ||
        setupResult.message === REQUEST_ERROR
      ) {
        throw new Error("Failed to complete account setup. Please try again.");
      }

      await signIn({
        email: emailAddress,
        password,
        onSuccess: async () => {
          // Straight to the portfolio: the header picks the default company from the
          // claims the admin's company assignment produced.
          navigate("/portfolio", { replace: true });
        },
        onFailure: async () => {
          setStatus({
            type: "error",
            message: "Account created, but sign-in failed. Please sign in manually.",
          });
        },
      });
    } catch (error: any) {
      log.error("Activation password setup failed", error);
      setStatus({ type: "error", message: error?.message || "Activation failed." });
    } finally {
      setLoading(false);
    }
  };

  const codeForm = (
    <>
      <p className="text-sm text-gray-600 mt-1">
        Enter the email address your invitation was sent to, along with the 6-digit
        code from that email.
      </p>
      <div className="mt-4 space-y-3 text-sm">
        <div>
          <label className="block text-xs text-gray-600 mb-1" htmlFor="activation-email">
            Email
          </label>
          <input
            id="activation-email"
            type="email"
            value={emailAddress}
            onChange={(event) => setEmailAddress(event.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1" htmlFor="activation-code">
            Verification Code
          </label>
          <input
            id="activation-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </div>
      </div>
      <button
        type="button"
        onClick={submitCode}
        className="mt-4 bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm w-full"
        disabled={loading}
      >
        {loading ? "Verifying..." : "Verify Code"}
      </button>
      <button
        type="button"
        onClick={resendCode}
        className="mt-3 text-xs text-gray-600 hover:underline w-full disabled:no-underline disabled:text-gray-400"
        disabled={isResending || cooldownSeconds > 0}
      >
        {cooldownSeconds > 0
          ? `Resend code in ${cooldownSeconds}s`
          : isResending
            ? "Sending..."
            : "Didn't get a code? Send a new one"}
      </button>
    </>
  );

  const passwordForm = (
    <>
      <p className="text-sm text-gray-600 mt-1">
        Choose a password to finish setting up your account.
      </p>
      <div className="mt-4 text-sm">
        <ZeosPasswordInput
          name="password"
          label="Password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          showRules={true}
          isVisible={passwordVisible}
          syncVisibility={true}
          toggleVisibility={() => setPasswordVisible(!passwordVisible)}
          required={true}
        />
        <ZeosPasswordInput
          name="confirmPassword"
          label="Confirm Password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          isVisible={passwordVisible}
          syncVisibility={true}
          toggleVisibility={() => setPasswordVisible(!passwordVisible)}
          required={true}
        />
      </div>
      <button
        type="button"
        onClick={submitPassword}
        className="mt-4 bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm w-full"
        disabled={loading}
      >
        {loading ? "Setting up..." : "Create Password"}
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-zeos-white-md flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Activate Your Account</h1>
        {status && (
          <div
            className={`mt-3 text-sm ${
              status.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {status.message}
          </div>
        )}
        {phase === "code" ? codeForm : passwordForm}
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mt-3 text-xs text-gray-600 hover:underline w-full"
        >
          Back to login
        </button>
      </div>
    </div>
  );
};
